import React, { useState, useEffect } from "react";
import { Star, XCircle, AlertTriangle, User, Home, Info } from "lucide-react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

/**
 * SpecialBookingForm - Form for creating special/admin bookings with discounts
 * Updated to include:
 * - KJ Assignment dropdown with "No Preference" option
 * - Block/Unit field for resident identification
 * - Admin Note field for audit trail
 * - 5-booking limit warning with override option
 */
const SpecialBookingForm = ({ 
  onSuccess, 
  onClose 
}) => {
  const [kjList, setKjList] = useState([]);
  const [loadingKjs, setLoadingKjs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeBookingsWarning, setActiveBookingsWarning] = useState(null);
  const [checkingBookings, setCheckingBookings] = useState(false);
  
  const [formData, setFormData] = useState({
    member_name: "",
    member_email: "",
    member_phone: "",
    block_unit: "",
    date: "",
    slot_type: "afternoon",
    discount_type: "none",
    kj_required: false,
    kj_id: "",
    admin_note: "",
    force_override_limit: false
  });

  // Fetch KJ list on mount
  useEffect(() => {
    fetchKjList();
  }, []);

  const fetchKjList = async () => {
    setLoadingKjs(true);
    try {
      const response = await axios.get(`${API}/kj-list`);
      setKjList(response.data.kj_details || []);
    } catch (error) {
      console.error("Failed to fetch KJ list", error);
    } finally {
      setLoadingKjs(false);
    }
  };

  // Check active bookings when email or phone changes
  useEffect(() => {
    const checkActiveBookings = async () => {
      if (!formData.member_email && !formData.member_phone) {
        setActiveBookingsWarning(null);
        return;
      }
      
      setCheckingBookings(true);
      try {
        // Build query params
        const params = new URLSearchParams();
        if (formData.member_email) params.append('email', formData.member_email);
        if (formData.member_phone) params.append('phone', formData.member_phone);
        
        const response = await axios.get(`${API}/bookings/active-count?${params.toString()}`);
        const count = response.data.active_count || 0;
        
        if (count >= 5) {
          setActiveBookingsWarning({
            count,
            message: `This guest has ${count} active bookings (limit is 5). You can override if necessary.`
          });
        } else if (count >= 3) {
          setActiveBookingsWarning({
            count,
            message: `This guest has ${count} active bookings.`,
            isWarning: true
          });
        } else {
          setActiveBookingsWarning(null);
        }
      } catch (error) {
        // Endpoint may not exist yet, silently fail
        console.log("Active booking check not available");
        setActiveBookingsWarning(null);
      } finally {
        setCheckingBookings(false);
      }
    };
    
    const debounce = setTimeout(checkActiveBookings, 500);
    return () => clearTimeout(debounce);
  }, [formData.member_email, formData.member_phone]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Get selected KJ name for display
      const selectedKj = kjList.find(kj => kj.member_id === formData.kj_id);
      
      const payload = {
        member_name: formData.member_name,
        member_email: formData.member_email,
        member_phone: formData.member_phone || null,
        block_unit: formData.block_unit || null,
        date: formData.date,
        slot_type: formData.slot_type,
        discount_type: formData.discount_type,
        kj_required: formData.kj_required,
        kj_id: formData.kj_required && formData.kj_id ? formData.kj_id : null,
        kj_name: selectedKj ? selectedKj.name : null,
        admin_note: formData.admin_note || null,
        force_override_limit: formData.force_override_limit
      };
      
      const response = await axios.post(`${API}/admin/special-booking`, payload);
      
      toast.success(`Special booking ${response.data.booking.booking_code} created successfully!`);
      
      // Show warning if override was used
      if (response.data.active_bookings_warning) {
        toast.warning(response.data.active_bookings_warning);
      }
      
      onSuccess && onSuccess(response.data);
      onClose && onClose();
      
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create special booking";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="brutal-card bg-amber-50 border-amber-300">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-amber-700 flex items-center gap-2">
          <Star className="text-amber-500" /> Create Special Booking
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-black">
          <XCircle size={24} />
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Guest Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold mb-1">Guest Name *</label>
            <input
              type="text"
              className="brutal-input w-full"
              value={formData.member_name}
              onChange={(e) => setFormData({...formData, member_name: e.target.value})}
              required
              data-testid="special-guest-name"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Email *</label>
            <input
              type="email"
              className="brutal-input w-full"
              value={formData.member_email}
              onChange={(e) => setFormData({...formData, member_email: e.target.value})}
              required
              data-testid="special-guest-email"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Phone</label>
            <input
              type="tel"
              className="brutal-input w-full"
              value={formData.member_phone}
              onChange={(e) => setFormData({...formData, member_phone: e.target.value})}
              data-testid="special-guest-phone"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Block/Unit</label>
            <input
              type="text"
              className="brutal-input w-full"
              placeholder="e.g., 212 #01-123"
              value={formData.block_unit}
              onChange={(e) => setFormData({...formData, block_unit: e.target.value})}
              data-testid="special-block-unit"
            />
          </div>
        </div>
        
        {/* Active Bookings Warning */}
        {activeBookingsWarning && (
          <div className={`p-3 rounded border-2 ${activeBookingsWarning.count >= 5 ? 'bg-red-50 border-red-400' : 'bg-amber-50 border-amber-400'}`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className={activeBookingsWarning.count >= 5 ? 'text-red-600' : 'text-amber-600'} size={20} />
              <div className="flex-1">
                <p className={`text-sm font-bold ${activeBookingsWarning.count >= 5 ? 'text-red-700' : 'text-amber-700'}`}>
                  {activeBookingsWarning.message}
                </p>
                {activeBookingsWarning.count >= 5 && (
                  <label className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      checked={formData.force_override_limit}
                      onChange={(e) => setFormData({...formData, force_override_limit: e.target.checked})}
                      data-testid="force-override-checkbox"
                    />
                    <span className="text-sm text-red-700 font-bold">Override limit (Admin approval)</span>
                  </label>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Date & Slot */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold mb-1">Date *</label>
            <input
              type="date"
              className="brutal-input w-full"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              required
              data-testid="special-date"
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1">Time Slot *</label>
            <select
              className="brutal-input w-full"
              value={formData.slot_type}
              onChange={(e) => setFormData({...formData, slot_type: e.target.value})}
              data-testid="special-slot-type"
            >
              <option value="afternoon">1:00 PM - 5:00 PM</option>
              <option value="evening">6:00 PM - 10:00 PM</option>
            </select>
          </div>
        </div>
        
        {/* Discount Type */}
        <div>
          <label className="block text-sm font-bold mb-1">Discount Type *</label>
          <select
            className="brutal-input w-full"
            value={formData.discount_type}
            onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
            data-testid="special-discount-type"
          >
            <option value="none">Standard Rate</option>
            <option value="member_50">RN Member (50% Discount)</option>
            <option value="community_partner_50">Community Partner (50% Off)</option>
            <option value="own_events_100">Own Event (100% Free)</option>
          </select>
        </div>
        
        {/* Member Discount Info */}
        {formData.discount_type === 'member_50' && (
          <div className="bg-blue-50 border-2 border-blue-300 p-3 rounded text-sm">
            <p className="text-blue-800 flex items-start gap-2">
              <Info size={16} className="mt-0.5 shrink-0" />
              <span>
                <strong>Member Discount Rules:</strong> Each member can use the 50% discount once per calendar year (Jan 1 - Dec 31).
                The system validates by phone number or email against <strong>completed</strong> bookings only.
              </span>
            </p>
          </div>
        )}
        
        {/* Own Events Info */}
        {formData.discount_type === 'own_events_100' && (
          <div className="bg-green-50 border-2 border-green-300 p-3 rounded text-sm">
            <p className="text-green-800">
              <strong>✅ Auto-Confirmed:</strong> Own Events bookings are automatically marked as PAID ($0 balance).
            </p>
          </div>
        )}
        
        {/* KJ Assignment Section */}
        <div className="border-t-2 border-amber-300 pt-4">
          <label className="block text-sm font-bold mb-2">KJ Requirement</label>
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 bg-white border-2 rounded cursor-pointer hover:bg-purple-50">
              <input
                type="radio"
                name="kj_requirement"
                checked={formData.kj_required}
                onChange={() => setFormData({...formData, kj_required: true, kj_id: ''})}
                className="w-5 h-5"
                data-testid="kj-required-yes"
              />
              <div>
                <span className="font-bold text-purple-700">🎤 KJ Required</span>
                <p className="text-xs text-gray-500">Session will have KJ assistance</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 bg-white border-2 rounded cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="kj_requirement"
                checked={!formData.kj_required}
                onChange={() => setFormData({...formData, kj_required: false, kj_id: ''})}
                className="w-5 h-5"
                data-testid="kj-required-no"
              />
              <div>
                <span className="font-bold text-gray-700">🏠 No KJ Required (Private Session)</span>
                <p className="text-xs text-gray-500">Self-service karaoke without KJ involvement</p>
              </div>
            </label>
          </div>
          
          {formData.kj_required && (
            <div className="mt-3">
              <label className="block text-sm font-bold mb-1">Assign KJ</label>
              {loadingKjs ? (
                <div className="text-center py-2">
                  <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : (
                <select
                  className="brutal-input w-full"
                  value={formData.kj_id}
                  onChange={(e) => setFormData({...formData, kj_id: e.target.value})}
                  data-testid="kj-assignment-select"
                >
                  <option value="">-- No Preference (Pending Allocation) --</option>
                  {kjList.map(kj => (
                    <option key={kj.member_id} value={kj.member_id}>
                      {kj.type === 'External KJ' ? '🎤 ' : '🏠 '}{kj.name} - {kj.type}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-gray-500 mt-1">🎤 = External KJ ($35 allowance) | 🏠 = RN Member ($0)</p>
              {!formData.kj_id && formData.kj_required && (
                <p className="text-xs text-amber-600 mt-1 font-bold">
                  ⚠️ Booking will be marked as &quot;NEEDS KJ&quot; for later assignment
                </p>
              )}
            </div>
          )}
          
          {!formData.kj_required && (
            <div className="mt-3 bg-gray-100 border-2 border-gray-300 p-3 rounded text-sm">
              <p className="text-gray-700">
                <strong>ℹ️ Private Session:</strong> This booking will not appear in KJ assignment lists or KJ Ops reports.
              </p>
            </div>
          )}
        </div>
        
        {/* Admin Note */}
        <div>
          <label className="block text-sm font-bold mb-1">
            Admin Note <span className="text-gray-500 font-normal">(Internal - Not visible to guest)</span>
          </label>
          <textarea
            className="brutal-input w-full"
            rows={2}
            placeholder="e.g., Payment reference, special instructions, override reason..."
            value={formData.admin_note}
            onChange={(e) => setFormData({...formData, admin_note: e.target.value})}
            data-testid="admin-note-input"
          />
        </div>
        
        <button 
          type="submit" 
          className="w-full brutal-btn-primary flex items-center justify-center gap-2"
          disabled={submitting || (activeBookingsWarning?.count >= 5 && !formData.force_override_limit)}
          data-testid="create-special-booking-btn"
        >
          {submitting ? (
            <>
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              Creating...
            </>
          ) : (
            'CREATE SPECIAL BOOKING'
          )}
        </button>
      </form>
    </div>
  );
};

export default SpecialBookingForm;
